import React, { useMemo } from 'react';
import { Collapse, Flex, Table, Empty } from 'antd';
import { CustomTitle } from '@/components';

/* ============================================================
   Types
============================================================ */

interface OperatingHourRow {
  day: string;
  display_time: string;
  is_open: boolean;
}

type OperatingHoursCategory = OperatingHourRow[];

interface Facility {
  name: string;
  street_address_1: string;
  street_address_2?: string;
  country: string;
  city?: string;
  state: string;
  zip_code: string;
  operating_hours: Record<string, OperatingHoursCategory>;
}

interface WarehouseTabProps {
  facility: Facility;
}

/* ============================================================
   Component
============================================================ */

export const WarehouseTab: React.FC<WarehouseTabProps> = ({ facility }) => {
  const address = {
    line_1: facility.street_address_1,
    line_2: facility.street_address_2,
    country: facility.country,
    city: facility.city || '',
    state: facility.state,
    zipcode: facility.zip_code,
  };

  /* ----------------- Render Operating Hours Table ----------------- */
  const renderHoursTable = (hoursData: OperatingHoursCategory) => {
    if (!Array.isArray(hoursData) || hoursData.length === 0) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    const dataSource = hoursData.map((row, idx) => ({
      key: idx,
      day: row.day,
      time: row.display_time,
      isOpen: row.is_open,
    }));

    const columns = [
      {
        title: 'Day',
        dataIndex: 'day',
        key: 'day',
        render: (text: string, record: { time: string }) => (
          <Flex justify='space-between'>
            <CustomTitle
              text={text || '--'}
              fontSize='12px'
              fontWeight='400'
              lineHeight='20px'
              color='var(--text-primary)'
              marginBottom='0'
              marginTop='0'
            />
            <CustomTitle
              text={record.time || '--'}
              fontSize='12px'
              fontWeight='400'
              lineHeight='20px'
              color='var(--text-primary)'
              marginBottom='0'
              marginTop='0'
            />
          </Flex>
        ),
      },
    ];

    return (
      <Table
        className='zl-AA-operation-hours-table'
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        rowHoverable={false}
        showHeader={false}
        bordered
        size='middle'
        style={{ borderRadius: 12 }}
      />
    );
  };

  /* ----------------- Collapse Items ----------------- */
  const collapseItems = useMemo(() => {
    if (!facility?.operating_hours) return [];

    return Object.entries(facility.operating_hours).map(
      ([label, hours], idx) => ({
        key: String(idx),
        label: <span className='text-capitalize'>{label}</span>,
        children: renderHoursTable(hours),
      })
    );
  }, [facility?.operating_hours]);

  return (
    <Flex vertical gap={0}>
      {/* Warehouse Name */}
      <CustomTitle
        text={`${facility.name} – All Shipments`}
        fontSize='20px'
        fontWeight='700'
        lineHeight='28px'
        color='var(--text-primary)'
        marginBottom='0'
        marginTop='0'
      />

      {/* Address */}
      <CustomTitle
        text={
          <>
            {`${address.line_1}\n ${
              address.line_2 ? `${address.line_2}\n` : ''
            }${address.country}, ${address.city} ${address.state} ${
              address.zipcode
            }`
              .split('\n')
              .map((line, idx) => (
                <React.Fragment key={idx}>
                  {line}
                  <br />
                </React.Fragment>
              ))}
          </>
        }
        fontSize='12px'
        fontWeight='400'
        lineHeight='20px'
        color='var(--text-primary)'
        marginTop='4px'
        marginBottom='28px'
      />

      <CustomTitle
        text='Operating hours'
        fontSize='16px'
        fontWeight='600'
        lineHeight='20px'
        color='var(--text-primary)'
        marginTop='0'
        marginBottom='12px'
      />

      {/* Dynamic collapse */}
      {collapseItems.length > 0 ? (
        <Collapse
          className='zl-custom-collapse zl-custom-collapse-AA delta-airline Lufthansa-tabs'
          accordion
          items={collapseItems}
        />
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      )}
    </Flex>
  );
};
